import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, useParams, Link } from "react-router-dom";
import BlogApi from "../Api/BlogApi";
import ImageUploadApi from "../Api/ImageUploadApi";
import { toast } from "react-toastify";
import { IMAGE_BASE_URL } from "../Config/Index";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import StandardDatePicker from "./StandardDatePicker";

const BlogFormLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        slug: "",
        publishDate: new Date().toISOString(),
        status: true,
        description: "",
        displayOrder: 0,
        metaTitle: "",
        metaKeywords: "",
        metaDescription: ""
    });
    const [imageFile, setImageFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [savedImage, setSavedImage] = useState(null);
    const [errors, setErrors] = useState({});

    const statusOptions = [
        { value: true, label: "Active" },
        { value: false, label: "Inactive" },
    ];

    useEffect(() => {
        if (isEditMode) {
            fetchBlogDetails();
        }
    }, [id]);

    const fetchBlogDetails = async () => {
        setLoading(true);
        try {
            const res = await BlogApi.getBlogById(id);
            if (res.status) {
                const data = res.response.data;
                setFormData({
                    title: data.title || "",
                    slug: data.slug || "",
                    publishDate: data.publishDate || new Date().toISOString(),
                    status: data.status ?? true,
                    description: data.description || "",
                    displayOrder: data.displayOrder || 0,
                    metaTitle: data.metaTitle || "",
                    metaKeywords: data.metaKeywords || "",
                    metaDescription: data.metaDescription || ""
                });
                if (data.image) {
                    setSavedImage(data.image);
                    setPreviewImage(`${IMAGE_BASE_URL}/${data.image.path}`);
                }
            }
        } catch (error) {
            console.error("Error fetching blog details:", error);
        } finally {
            setLoading(false);
        }
    };

    const generateSlug = (text) => {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-') // Replace spaces with -
            .replace(/[^\w-]+/g, '') // Remove all non-word chars
            .replace(/--+/g, '-'); // Replace multiple - with single -
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === "displayOrder") {
            newValue = Math.max(0, parseInt(value) || 0);
        }

        setFormData(prev => {
            const updated = { ...prev, [name]: newValue };
            if (name === "title" && !isEditMode) {
                updated.slug = generateSlug(value);
            }
            return updated;
        });

        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSelectChange = (selectedOption, { name }) => {
        setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : true }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleDateChange = (e) => {
        setFormData(prev => ({ ...prev, publishDate: e.target.value }));
        if (errors.publishDate) setErrors(prev => ({ ...prev, publishDate: "" }));
    };

    const handleEditorChange = (content) => {
        setFormData(prev => ({ ...prev, description: content }));
        if (errors.description) setErrors(prev => ({ ...prev, description: "" }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setPreviewImage(reader.result);
            reader.readAsDataURL(file);
            setErrors(prev => ({ ...prev, image: "" }));
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setPreviewImage(null);
        setSavedImage(null);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (!formData.slug.trim()) newErrors.slug = "Slug is required";
        if (!formData.publishDate) newErrors.publishDate = "Publish date is required";
        if (!formData.description.trim() || formData.description === "<p><br></p>") newErrors.description = "Description is required";
        if (!formData.displayOrder && formData.displayOrder !== 0) newErrors.displayOrder = "Display Order is required";
        if (!isEditMode && !imageFile) newErrors.image = "Image is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            let finalImage = savedImage;
            if (imageFile) {
                const form = new FormData();
                form.append("file", imageFile);
                const uploadRes = await ImageUploadApi.uploadImage({ formData: form, path: "blogs" });
                if (uploadRes.status) {
                    finalImage = uploadRes.response.data; // Matches { fileName, path, originalName }
                } else {
                    setLoading(false);
                    return;
                }
            }

            const payload = {
                ...formData,
                image: finalImage
            };

            let res;
            if (isEditMode) {
                res = await BlogApi.updateBlog(id, payload);
            } else {
                res = await BlogApi.createBlog(payload);
            }

            if (res.status) {
                // toast.success(isEditMode ? "Blog updated successfully" : "Blog added successfully");
                navigate("/blog");
            }
        } catch (error) {
            console.error("Error submitting blog:", error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const modules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"],
            ["clean"],
        ],
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-primary-600 pb-2 mb-0" style={{ color: '#002B5B' }}>{isEditMode ? "Edit Blog" : "Add New Blog"}</h6>
            </div>
            <div className="card-body p-24">
                <form onSubmit={handleSubmit} className="pb-40">
                    <div className="row gy-4">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Title<span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.title ? 'is-invalid' : ''}`}
                                name="title"
                                placeholder="Enter Blog Title"
                                value={formData.title}
                                onChange={handleChange}
                            />
                            {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Slug<span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.slug ? 'is-invalid' : ''}`}
                                name="slug"
                                placeholder="Enter Slug"
                                value={formData.slug}
                                onChange={handleChange}
                            />
                            {errors.slug && <div className="invalid-feedback">{errors.slug}</div>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Publish Date<span className="text-danger">*</span></label>
                            <StandardDatePicker
                                name="publishDate"
                                value={formData.publishDate}
                                onChange={handleDateChange}
                                className={`form-control radius-8 ${errors.publishDate ? 'is-invalid' : ''}`}
                            />
                            {errors.publishDate && <div className="text-danger mt-1" style={{ fontSize: '12px' }}>{errors.publishDate}</div>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Status<span className="text-danger">*</span></label>
                            <Select
                                name="status"
                                options={statusOptions}
                                value={statusOptions.find((opt) => opt.value === formData.status)}
                                onChange={handleSelectChange}
                                styles={selectStyles(errors.status)}
                                isClearable={false}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Display Order</label>
                            <input
                                type="number"
                                className="form-control radius-8"
                                name="displayOrder"
                                placeholder="Enter Display Order"
                                value={formData.displayOrder}
                                onChange={handleChange}
                                min="0"
                                onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Image<span className="text-danger">{!isEditMode ? "*" : ""}</span></label>
                            {!previewImage && (
                                <div className="position-relative">
                                    <div className="d-flex align-items-center gap-3">
                                        <label className="btn btn-outline-neutral-400 radius-8 px-24 py-11 mb-0 cursor-pointer" htmlFor="imageUpload">
                                            Choose File
                                        </label>
                                        <span className="text-secondary-light">No file chosen</span>
                                        <input
                                            id="imageUpload"
                                            type="file"
                                            className="d-none"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                        />
                                    </div>
                                    {errors.image && <small className="text-danger">{errors.image}</small>}
                                </div>
                            )}
                            {previewImage && (
                                <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light-600">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-100-px h-100-px rounded-8 overflow-hidden border">
                                            <img src={previewImage} alt="Preview" className="w-100 h-100 object-fit-cover" />
                                        </div>
                                    </div>
                                    <button type="button" className="btn btn-icon btn-primary-100 text-danger-600 rounded-circle" onClick={handleRemoveImage} title="Delete Image">
                                        <Icon icon="mingcute:delete-2-line" width="24" height="24" />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="col-12 mt-4">
                            <h6 className="text-md fw-bold mb-3 border-bottom pb-2">SEO Details</h6>
                            <div className="row gy-4">
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold text-secondary-light">Meta Title</label>
                                    <input
                                        type="text"
                                        className="form-control radius-8"
                                        name="metaTitle"
                                        placeholder="Enter Meta Title"
                                        value={formData.metaTitle}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold text-secondary-light">Meta Keywords</label>
                                    <input
                                        type="text"
                                        className="form-control radius-8"
                                        name="metaKeywords"
                                        placeholder="Enter Meta Keywords (comma separated)"
                                        value={formData.metaKeywords}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-12">
                                    <label className="form-label fw-semibold text-secondary-light">Meta Description</label>
                                    <textarea
                                        className="form-control radius-8"
                                        name="metaDescription"
                                        rows="3"
                                        placeholder="Enter Meta Description"
                                        value={formData.metaDescription}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-12 mt-4">
                            <label className="form-label fw-semibold text-secondary-light">Description<span className="text-danger">*</span></label>
                            <div className={errors.description ? 'border border-danger' : 'border'}>
                                <style>{`
                                    .ql-container {
                                        min-height: 250px;
                                        font-family: 'Inter', sans-serif;
                                        font-size: 16px;
                                    }
                                    .ql-editor {
                                        min-height: 250px;
                                    }
                                `}</style>
                                <ReactQuill
                                    theme="snow"
                                    value={formData.description}
                                    onChange={handleEditorChange}
                                    modules={modules}
                                    placeholder="Enter Description"
                                />
                            </div>
                            {errors.description && <div className="text-danger mt-1" style={{ fontSize: '12px' }}>{errors.description}</div>}
                        </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-24">
                        <Link to="/blog" className="btn btn-outline-danger-600 px-32 radius-8 justify-content-center d-flex align-items-center" style={{ width: "120px", height: "48px" }}>Cancel</Link>
                        <button type="submit" className="btn btn-primary radius-8 px-18 py-11 justify-content-center d-flex align-items-center" disabled={loading} style={{ backgroundColor: "#D81B60", borderColor: "#D81B60", width: "120px", height: "48px" }}>
                            {loading ? "Saving..." : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BlogFormLayer;
